'use client';

import { CSSProperties, KeyboardEvent, useRef } from 'react';

import { cn } from '@/lib/utils';

import { EdgeFade, type EdgeFadeSide } from '@/components/atoms/EdgeFade';

import {
  MOBILE_EDGE_BLEED_CLASSES,
  MOBILE_PAGE_EDGE_INSET_CLASSES,
  PEEK_PAGE_WIDTH_CLASS,
  VIEWPORT_CLASSES,
} from './constants';
import { PagedCarouselControls } from './PagedCarouselControls';
import { PagedCarouselClassNames, PagedCarouselRenderItem } from './types';
import { scrollToPage, useActivePage } from './useActivePage';
import { useFittedItemsPerPage } from './useFittedItemsPerPage';
import { chunkItems, getPageRowClassName } from './utils';

const PEEK_CARD_FRACTION = 0.35;

type PagedCarouselInstanceProps<T> = {
  items: T[];
  itemsPerPage?: number;
  renderItem: PagedCarouselRenderItem<T>;
  'aria-label': string;
  showEdgeFade?: boolean;
  peekPreviewSurroundingPages?: boolean;
  itemWidth?: string;
  gap?: number;
  classNames?: PagedCarouselClassNames;
};

export function PagedCarouselInstance<T>({
  items,
  itemsPerPage: itemsPerPageProp,
  renderItem,
  'aria-label': ariaLabel,
  showEdgeFade = false,
  peekPreviewSurroundingPages = false,
  itemWidth,
  gap = 1.5,
  classNames,
}: PagedCarouselInstanceProps<T>) {
  const viewportRef = useRef<HTMLUListElement>(null);
  const maxItemsPerPage = Math.max(itemsPerPageProp ?? items.length, 1);
  const itemsPerPage = useFittedItemsPerPage(
    viewportRef,
    maxItemsPerPage,
    itemWidth
  );
  const pages = chunkItems<T>(items, itemsPerPage);
  const pageCount = pages.length;
  const activePage = useActivePage(viewportRef, pageCount);

  const isFixedWidth = Boolean(itemWidth);
  const gapValue = `${gap}rem`;

  const getGridColumnsStyle = (columnCount: number) =>
    isFixedWidth
      ? { gridTemplateColumns: `repeat(${columnCount}, ${itemWidth})` }
      : undefined;

  const peekWidth =
    peekPreviewSurroundingPages && !isFixedWidth
      ? `${(PEEK_CARD_FRACTION / (itemsPerPage + PEEK_CARD_FRACTION)) * 100}%`
      : '0px';

  const a11yHandleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollToPage(viewportRef, activePage - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollToPage(viewportRef, activePage + 1);
    }
  };

  if (items.length === 0) {
    return null;
  }

  if (pageCount === 1) {
    return (
      <ul
        ref={viewportRef}
        aria-label={ariaLabel}
        style={{ gap: gapValue, ...getGridColumnsStyle(items.length) }}
        className={cn(
          'grid w-full',
          !isFixedWidth && getPageRowClassName(itemsPerPage),
          classNames?.root
        )}
      >
        {items.map((item, index) => (
          <li key={index} data-paged-carousel-item className='h-full'>
            {renderItem({ item, index, isGrid: true })}
          </li>
        ))}
      </ul>
    );
  }

  const fadeSides: EdgeFadeSide[] = [
    ...(activePage > 0 ? (['left'] as const) : []),
    ...(activePage < pageCount - 1 ? (['right'] as const) : []),
  ];

  const viewport = (
    <ul
      ref={viewportRef}
      onKeyDown={a11yHandleKeyDown}
      tabIndex={0}
      role='region'
      aria-roledescription='carousel'
      aria-label={ariaLabel}
      style={
        {
          '--paged-carousel-peek': peekWidth,
          gap: gapValue,
        } as CSSProperties
      }
      className={cn(VIEWPORT_CLASSES, classNames?.viewport)}
    >
      {pages.map((pageItems, pageIndex) => (
        <li
          key={pageIndex}
          data-paged-carousel-page
          aria-label={`Page ${pageIndex + 1} of ${pageCount}`}
          className={cn(
            'shrink-0 snap-center',
            MOBILE_PAGE_EDGE_INSET_CLASSES,
            isFixedWidth ? 'w-max' : PEEK_PAGE_WIDTH_CLASS,
            classNames?.page
          )}
        >
          <div
            style={{
              gap: gapValue,
              ...getGridColumnsStyle(pageItems.length),
            }}
            className={cn(
              'grid',
              !isFixedWidth && getPageRowClassName(itemsPerPage),
              classNames?.pageRow
            )}
          >
            {pageItems.map((item, itemIndex) => {
              const globalIndex = pageIndex * itemsPerPage + itemIndex;

              return (
                <div
                  key={globalIndex}
                  data-paged-carousel-item
                  className='h-full'
                >
                  {renderItem({ item, index: globalIndex, isGrid: false })}
                </div>
              );
            })}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-8',
        classNames?.root
      )}
    >
      {showEdgeFade ? (
        <EdgeFade sides={fadeSides} className={cn(MOBILE_EDGE_BLEED_CLASSES)}>
          {viewport}
        </EdgeFade>
      ) : (
        <div className='relative w-full'>{viewport}</div>
      )}
      <PagedCarouselControls
        viewportRef={viewportRef}
        pageCount={pageCount}
        activePage={activePage}
        classNames={classNames}
      />
    </div>
  );
}
